import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Checkbox,
  Chip,
  Container,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  LinearProgress,
} from '@mui/material';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DangerousIcon from '@mui/icons-material/Dangerous';
import StorefrontIcon from '@mui/icons-material/Storefront';
import RefreshIcon from '@mui/icons-material/Refresh';
import ClearIcon from '@mui/icons-material/Clear';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import VerifiedUserRoundedIcon from '@mui/icons-material/VerifiedUserRounded';
import WorkspacePremiumRoundedIcon from '@mui/icons-material/WorkspacePremiumRounded';
import RecommendRoundedIcon from '@mui/icons-material/RecommendRounded';
import InventoryRoundedIcon from '@mui/icons-material/InventoryRounded';
import AssuredWorkloadRoundedIcon from '@mui/icons-material/AssuredWorkloadRounded';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { alpha } from '@mui/material/styles';
import { useTheme, useMediaQuery } from '@mui/material';

import {
  getBusinesses,
  getBranches,
  getDocuments,
  getContacts,
  listReports,
} from '../api';
import { useAuth } from '../context/AuthContext';
import { useSelection } from '../context/SelectionContext';
import { dedupeBusinesses, deriveBusinessDisplayName } from '../utils/business';
import { useNavigate } from 'react-router-dom';
import { DOCUMENT_SECTIONS, FLATTENED_DOCUMENT_TITLES } from '../constants/documentCatalog';
import { REPORT_TYPES, REPORT_TYPE_LABELS } from '../constants/reportTemplates';

const STATUS_COLORS = {
  compliant: '#34c759',
  expiring: '#f8c94c',
  expired: '#ff3b30',
};

const CARD_PALETTE = [
  { from: '#E0EAFF', to: '#EFF3FF', icon: <VerifiedUserRoundedIcon color="primary" /> }, // FSSAI MANDATED DOCUMENTS - 1
  { from: '#E6FFF6', to: '#F2FFF9', icon: <WorkspacePremiumRoundedIcon color="success" /> }, // FSSAI MANDATED DOCUMENTS - 2
  { from: '#FFF3E0', to: '#FFF7ED', icon: <RecommendRoundedIcon sx={{ color: '#fb8c00' }} /> }, // FSSAI RECOMMENDED DOCUMENTS
  { from: '#F3E8FF', to: '#F8F5FF', icon: <InventoryRoundedIcon sx={{ color: '#8e24aa' }} /> }, // GMP RECOMMENDED DOCUMENTS
  { from: '#E0F2FE', to: '#F1F5F9', icon: <AssuredWorkloadRoundedIcon sx={{ color: '#0284c7' }} /> }, // HACCP DOCUMENTS
];

let dashboardCache = null;

const DashboardPage = () => {
  const { isAdmin, userRole, userBusinessId, userContactId, userBranchIds, user } = useAuth();
  const navigate = useNavigate();

  const userId = user?.uid || 'anonymous';
  const cachedState = dashboardCache && dashboardCache.userId === userId ? dashboardCache : null;
  const cacheMatchesContext = Boolean(
    cachedState && cachedState.isAdmin === isAdmin && cachedState.userBusinessId === userBusinessId,
  );

  const [businesses, setBusinesses] = useState(cachedState?.businesses || []);
  const [branches, setBranches] = useState(cachedState?.branches || []);
  const [branchesBusinessId, setBranchesBusinessId] = useState(cachedState?.branchesBusinessId || '');
  const [contacts, setContacts] = useState(cachedState?.contacts || []);
  const [documentsByBranch, setDocumentsByBranch] = useState(cachedState?.documentsByBranch || {});
  const [documentsBusinessId, setDocumentsBusinessId] = useState(cachedState?.documentsBusinessId || '');

  const {
    selectedBusinessId,
    setSelectedBusinessId,
    selectedBusinessName,
    selectedBranchId,
    setSelectedBranchId,
    clearBranch,
  } = useSelection();

  const initialBranchFilter = cachedState?.branchFilter ?? (selectedBranchId || 'ALL');
  const [branchFilter, setBranchFilter] = useState(initialBranchFilter);
  const [loading, setLoading] = useState(
    cachedState ? { businesses: false, branches: false, documents: false } : { businesses: true, branches: false, documents: false },
  );
  const [businessesReady, setBusinessesReady] = useState(Boolean(cachedState && cacheMatchesContext));
  const [branchesReady, setBranchesReady] = useState(Boolean(cachedState && cacheMatchesContext));
  const [documentsReady, setDocumentsReady] = useState(Boolean(cachedState && cacheMatchesContext));
  const [error, setError] = useState('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const showBusinessSelect = isAdmin;

  useEffect(() => {
    let isMounted = true;

    const loadInitial = async () => {
      const skipLoading = businesses.length && cacheMatchesContext;
      if (!skipLoading) {
        setLoading(prev => ({ ...prev, businesses: true }));
        setBusinessesReady(false);
      }
      try {
        const response = await getBusinesses();
        if (!isMounted) return;
        const activeBusinesses = (response.data || []).filter(b => (b.status || 'ACTIVE') !== 'INACTIVE');
        const uniqueBusinesses = dedupeBusinesses(activeBusinesses);
        setBusinesses(uniqueBusinesses);
        if (selectedBusinessId && !selectedBusinessName) {
          const matched = uniqueBusinesses.find(b => b.businessId === selectedBusinessId);
          if (matched) {
            setSelectedBusinessId(matched.businessId, deriveBusinessDisplayName(matched));
          }
        }
        if (isAdmin) {
          if (uniqueBusinesses.length) {
            const exists = uniqueBusinesses.some(b => b.businessId === selectedBusinessId);
            if (!selectedBusinessId || !exists) {
              const fallbackBusiness = uniqueBusinesses[0];
              setSelectedBusinessId(fallbackBusiness.businessId, deriveBusinessDisplayName(fallbackBusiness));
            }
          } else {
            setSelectedBusinessId('');
          }
        } else if (userBusinessId) {
          const matchedBusiness = uniqueBusinesses.find(b => b.businessId === userBusinessId);
          if (matchedBusiness) {
            setSelectedBusinessId(matchedBusiness.businessId, deriveBusinessDisplayName(matchedBusiness));
          } else {
            setSelectedBusinessId('');
          }
        } else if (!selectedBusinessId) {
          const fallback = uniqueBusinesses[0];
          setSelectedBusinessId(
            fallback?.businessId || '',
            fallback ? deriveBusinessDisplayName(fallback) : '',
          );
        }
      } catch (err) {
        if (!isMounted) return;
        console.error('Failed to fetch businesses for dashboard:', err);
        setError('Could not load businesses.');
      } finally {
        if (!isMounted) return;
        setLoading(prev => ({ ...prev, businesses: false }));
        setBusinessesReady(true);
      }
    };

    loadInitial();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, userBusinessId]);

  useEffect(() => {
    let cancelled = false;

    const loadBranchesForBusiness = async () => {
      if (!selectedBusinessId) {
        setBranches([]);
        setContacts([]);
        setBranchesBusinessId('');
        setDocumentsByBranch({});
        setDocumentsBusinessId('');
        clearBranch();
        setLoading(prev => ({ ...prev, branches: false }));
        setBranchesReady(true);
        setDocumentsReady(true);
        return;
      }

      const cachedBranchesBusinessId = cachedState?.branchesBusinessId || '';
      const cachedSelectedBusinessId = cachedState?.selectedBusinessId || '';
      const cachedBranchCount = Array.isArray(cachedState?.branches) ? cachedState.branches.length : 0;
      const skipLoading =
        cacheMatchesContext &&
        cachedBranchesBusinessId === selectedBusinessId &&
        cachedSelectedBusinessId === selectedBusinessId &&
        cachedBranchCount > 0;
      if (!skipLoading) {
        setLoading(prev => ({ ...prev, branches: true }));
        setBranchesReady(false);
        setDocumentsReady(false);
      } else {
        setBranchesReady(true);
      }

      try {
        const [branchResponse, contactResponse] = await Promise.all([
          getBranches(selectedBusinessId),
          getContacts(selectedBusinessId),
        ]);
        if (cancelled) return;
        const activeBranchList = (branchResponse.data || []).filter(branch => (branch.status || 'ACTIVE') !== 'INACTIVE');
        setBranches(activeBranchList);
        setBranchesBusinessId(selectedBusinessId);
        setContacts(contactResponse.data || []);
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to load branches/contacts:', err);
        setError('Could not load branches for the selected business.');
        setBranches([]);
        setBranchesBusinessId('');
        setDocumentsByBranch({});
        setDocumentsBusinessId('');
        setContacts([]);
        setDocumentsReady(true);
      } finally {
        if (cancelled) return;
        setLoading(prev => ({ ...prev, branches: false }));
        setBranchesReady(true);
      }
    };

    loadBranchesForBusiness();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBusinessId, clearBranch, isAdmin, userBusinessId]);

  const effectiveUserContactId = useMemo(() => {
    if (userContactId) return userContactId;
    const match = contacts.find(contact => contact.firebaseUid === user?.uid);
    return match?.contactId || null;
  }, [contacts, userContactId, user]);

  const filteredBranches = useMemo(() => {
    if (!selectedBusinessId) return [];
    if (isAdmin || userRole === 'Business Owner') return branches;
    if (userRole === 'Supervisor' || userRole === 'Guest') {
      if (!effectiveUserContactId) return [];
      return branches.filter(branch => (branch.supervisorIds || []).includes(effectiveUserContactId));
    }
    return branches;
  }, [branches, isAdmin, userRole, effectiveUserContactId, selectedBusinessId]);

  useEffect(() => {
    if (!filteredBranches.length) {
      setDocumentsByBranch({});
      setDocumentsBusinessId('');
      setBranchFilter('ALL');
      clearBranch();
      return;
    }
    if (selectedBranchId && !filteredBranches.some(branch => branch.branchId === selectedBranchId)) {
      const fallback = filteredBranches[0].branchId;
      setSelectedBranchId(fallback);
    }
  }, [filteredBranches, selectedBranchId, clearBranch, setSelectedBranchId]);

  useEffect(() => {
    setBranchFilter(selectedBranchId || 'ALL');
  }, [selectedBranchId]);

  useEffect(() => {
    let cancelled = false;

    const loadDocuments = async () => {
      if (!filteredBranches.length) {
        setDocumentsByBranch({});
        setDocumentsBusinessId('');
        setLoading(prev => ({ ...prev, documents: false }));
        setDocumentsReady(true);
        return;
      }

      const cachedDocs = cachedState?.documentsByBranch || {};
      const cachedDocsBusinessId = cachedState?.documentsBusinessId || '';
      const cachedSelectedBusinessId = cachedState?.selectedBusinessId || '';
      const hasDocsForAllBranches =
        cacheMatchesContext &&
        cachedDocsBusinessId === selectedBusinessId &&
        cachedSelectedBusinessId === selectedBusinessId &&
        filteredBranches.every(branch => Object.prototype.hasOwnProperty.call(cachedDocs, branch.branchId));

      if (hasDocsForAllBranches) {
        setDocumentsReady(true);
        setLoading(prev => ({ ...prev, documents: false }));
        return;
      }

      setDocumentsReady(false);
      setLoading(prev => ({ ...prev, documents: true }));
      try {
        const entries = await Promise.all(
          filteredBranches.map(async branch => {
            try {
              const response = await getDocuments(branch.branchId, isAdmin ? selectedBusinessId : undefined);
              const docs = (response.data || []).map(doc => ({ ...doc, branchId: branch.branchId }));
              return [branch.branchId, docs];
            } catch (err) {
              console.error(`Failed to load documents for branch ${branch.branchId}:`, err);
              return [branch.branchId, []];
            }
          }),
        );
        if (cancelled) return;
        setDocumentsByBranch(Object.fromEntries(entries));
        setDocumentsBusinessId(selectedBusinessId);
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to load documents:', err);
        setError('Could not load documents for the selected filters.');
      } finally {
        if (cancelled) return;
        setLoading(prev => ({ ...prev, documents: false }));
        setDocumentsReady(true);
      }
    };

    loadDocuments();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredBranches, selectedBusinessId, isAdmin, userBusinessId]);

  const branchMap = useMemo(() => {
    const map = new Map();
    branches.forEach(branch => map.set(branch.branchId, branch));
    return map;
  }, [branches]);

  const activeBranchIds = useMemo(() => {
    if (branchFilter === 'ALL') {
      return filteredBranches.map(branch => branch.branchId);
    }
    return filteredBranches.some(branch => branch.branchId === branchFilter)
      ? [branchFilter]
      : [];
  }, [filteredBranches, branchFilter]);

  const aggregatedDocuments = useMemo(() => {
    return activeBranchIds.flatMap(id => documentsByBranch[id] || []);
  }, [activeBranchIds, documentsByBranch]);

  const selectedBusiness = useMemo(() => (
    businesses.find(b => b.businessId === selectedBusinessId) || null
  ), [businesses, selectedBusinessId]);

  const businessEligibility = useMemo(() => {
    if (!selectedBusiness) return {};
    if (selectedBusiness.documentEligibility?.sections) return selectedBusiness.documentEligibility.sections;
    if (selectedBusiness.documentEligibility && !selectedBusiness.documentEligibility.sections) return selectedBusiness.documentEligibility;
    return {};
  }, [selectedBusiness]);

  const sectionWeights = useMemo(() => {
    const raw = selectedBusiness?.documentSectionWeights || selectedBusiness?.documentEligibility?.weights || {};
    const fallback = 100 / DOCUMENT_SECTIONS.length;
    const weights = {};
    let sum = 0;
    DOCUMENT_SECTIONS.forEach((section) => {
      const value = Number(raw[section.key]);
      const w = Number.isFinite(value) && value > 0 ? value : fallback;
      weights[section.key] = w;
      sum += w;
    });
    const normalized = {};
    const divisor = sum > 0 ? sum : 100;
    DOCUMENT_SECTIONS.forEach((section) => {
      normalized[section.key] = weights[section.key] / divisor;
    });
    return { raw: weights, normalized };
  }, [selectedBusiness]);

  const summarySections = useMemo(() => {
    const eligibilitySections = businessEligibility && Object.keys(businessEligibility).length
      ? businessEligibility
      : null;
    const fallbackIds = FLATTENED_DOCUMENT_TITLES.map(item => item.documentTitleId);
    const eligibleIds = Array.isArray(selectedBusiness?.eligibleDocumentTitleIds) && selectedBusiness.eligibleDocumentTitleIds.length
      ? selectedBusiness.eligibleDocumentTitleIds
      : fallbackIds;
    const eligibleSet = new Set(eligibleIds);

    return DOCUMENT_SECTIONS.map((section, idx) => {
      const sectionIds = (eligibilitySections && Array.isArray(eligibilitySections[section.key]))
        ? eligibilitySections[section.key]
        : FLATTENED_DOCUMENT_TITLES
            .filter(item => item.sectionKey === section.key)
            .map(item => item.documentTitleId)
            .filter(id => eligibleSet.has(id));
      const uploaded = aggregatedDocuments.filter(doc => doc.documentTitleId && sectionIds.includes(doc.documentTitleId)).length;
      return {
        key: section.key,
        label: section.label,
        eligibleCount: sectionIds.length,
        uploadedCount: uploaded,
        index: idx,
      };
    });
  }, [selectedBusiness, aggregatedDocuments, businessEligibility]);

  const summaryTotals = useMemo(() => {
    const eligible = summarySections.reduce((acc, item) => acc + item.eligibleCount, 0);
    const uploaded = summarySections.reduce((acc, item) => acc + item.uploadedCount, 0);
    const progress = eligible ? Math.min(100, Math.round((uploaded / eligible) * 100)) : 0;
    return { eligible, uploaded, progress };
  }, [summarySections]);

  const complianceScore = useMemo(() => {
    let weighted = 0;
    summarySections.forEach((section) => {
      const weight = sectionWeights.normalized[section.key] || 0;
      const progress = section.eligibleCount ? section.uploadedCount / section.eligibleCount : 0;
      weighted += weight * progress;
    });
    return Math.round(weighted * 100);
  }, [summarySections, sectionWeights]);

  const statusCounts = useMemo(() => {
    const counts = { compliant: 0, expiring: 0, expired: 0 };
    aggregatedDocuments.forEach(doc => {
      const status = resolveDocumentStatus(doc.expirationDate);
      if (status === 'expired') counts.expired += 1;
      else if (status === 'expiring') counts.expiring += 1;
      else counts.compliant += 1;
    });
    return counts;
  }, [aggregatedDocuments]);

  const branchComplianceData = useMemo(() => {
    const totals = { compliant: 0, nonCompliant: 0 };
    filteredBranches.forEach(branch => {
      const docs = documentsByBranch[branch.branchId] || [];
      if (!docs.length) return;
      const hasExpired = docs.some(doc => resolveDocumentStatus(doc.expirationDate) === 'expired');
      const hasExpiring = docs.some(doc => resolveDocumentStatus(doc.expirationDate) === 'expiring');
      // if (!hasExpired && !hasExpiring) totals.compliant += 1;
      if (!hasExpired) totals.compliant += 1;
      else totals.nonCompliant += 1;
    });
    return totals;
  }, [filteredBranches, documentsByBranch]);

  const resolveBusinessName = useCallback(
    (businessId) => {
      const match = businesses.find(b => b.businessId === businessId);
      return deriveBusinessDisplayName(match);
    },
    [businesses],
  );

  const handleBusinessChange = (value) => {
    setBranchesReady(false);
    setDocumentsReady(false);
    setSelectedBusinessId(value, resolveBusinessName(value));
    clearBranch();
    setBranches([]);
    setContacts([]);
    setBranchesBusinessId('');
    setDocumentsByBranch({});
    setDocumentsBusinessId('');
    setBranchFilter('ALL');
  };

  const handleClearBusiness = () => {
    setSelectedBusinessId('');
    clearBranch();
    setBranches([]);
    setContacts([]);
    setBranchesBusinessId('');
    setDocumentsByBranch({});
    setDocumentsBusinessId('');
    setBranchFilter('ALL');
    setBranchesReady(true);
    setDocumentsReady(true);
  };

  const handleBranchChange = (value) => {
    if (value === 'ALL') {
      setBranchFilter('ALL');
      clearBranch();
    } else {
      setBranchFilter(value);
      setSelectedBranchId(value);
    }
  };

  useEffect(() => {
    if (!user) {
      dashboardCache = null;
    }
  }, [user]);

  useEffect(() => {
    dashboardCache = {
      userId,
      isAdmin,
      userBusinessId,
      businesses,
      branches,
      branchesBusinessId,
      contacts,
      documentsByBranch,
      documentsBusinessId,
      selectedBusinessId,
      selectedBranchId,
      branchFilter,
      timestamp: Date.now(),
    };
  }, [
    userId,
    isAdmin,
    userBusinessId,
    businesses,
    branches,
    branchesBusinessId,
    contacts,
    documentsByBranch,
    documentsBusinessId,
    selectedBusinessId,
    selectedBranchId,
    branchFilter,
  ]);

  const handleClearBranchFilter = () => {
    setBranchFilter('ALL');
    clearBranch();
    if (filteredBranches.length) {
      setDocumentsReady(false);
    }
  };

  const recentExpiringDocuments = useMemo(() => {
    const now = new Date();
    const upcoming = [];
    aggregatedDocuments.forEach(doc => {
      const expire = new Date(doc.expirationDate);
      if (Number.isNaN(expire.getTime())) return;
      const diffDays = Math.floor((expire - now) / (1000 * 60 * 60 * 24));
      // if (diffDays >= 0 && diffDays <= 30) {
      if (diffDays <= 30) {  
        upcoming.push({
          branchId: doc.branchId,
          documentTitle: doc.documentTitle,
          expirationDate: doc.expirationDate,
          status: resolveDocumentStatus(doc.expirationDate),
          diffDays,
        });
      }
    });
    upcoming.sort((a, b) => a.diffDays - b.diffDays);
    return upcoming;
  }, [aggregatedDocuments]);

  const supervisorBranchCount = userRole === 'Supervisor' ? filteredBranches.length : 0;
  const supervisorHasMultipleBranches = userRole === 'Supervisor' && supervisorBranchCount > 1;
  const showBranchSelect = userRole === 'Supervisor' ? supervisorHasMultipleBranches : true;
  const showTotalBranchesTile = userRole === 'Supervisor' ? supervisorHasMultipleBranches : true;
  const showBranchComplianceChart = userRole === 'Supervisor' ? supervisorHasMultipleBranches : true;
  const totalBranchesCount = filteredBranches.length;

  const goToDocuments = useCallback((options = {}) => {
    const { statusFilter, branchId, branchLabel, searchQuery } = options;
    const navigationState = {
      businessId: selectedBusinessId || null,
      businessName: selectedBusinessId ? resolveBusinessName(selectedBusinessId) : '',
    };

    if (statusFilter) {
      navigationState.statusFilter = statusFilter;
    }
    if (branchId) {
      navigationState.branchId = branchId;
      if (branchLabel) {
        navigationState.branchLabel = branchLabel;
      }
    }
    if (searchQuery) {
      navigationState.searchQuery = searchQuery;
    }

    navigate('/documents', { state: navigationState });
  }, [navigate, resolveBusinessName, selectedBusinessId]);

  const handleStatsTileClick = useCallback((tile) => {
    if (!tile) return;
    if (tile.statusFilter) {
      goToDocuments({ statusFilter: tile.statusFilter });
      return;
    }
    goToDocuments();
  }, [goToDocuments]);

  const handleRecentDocumentClick = useCallback((row) => {
    if (!row) return;
    const branch = branchMap.get(row.branchId);
    goToDocuments({
      statusFilter: resolveDocumentStatus(row.expirationDate),
      branchId: row.branchId,
      branchLabel: branch?.nickname || branch?.address || '',
      searchQuery: row.documentTitle,
    });
  }, [branchMap, goToDocuments]);

  const showInitialLoader = !error && (!businessesReady || !branchesReady || !documentsReady);

  if (userRole === 'Employee') {
    return <EmployeeDashboard />;
  }

  if (showInitialLoader) {
    return <DashboardLoadingHero />;
  }

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 3, md: 5 } }}>
      <Stack spacing={4}>
        {error && <Alert severity="error">{error}</Alert>}

        <Paper
          elevation={4}
          sx={(theme) => ({
            p: { xs: 2.5, sm: 3 },
            borderRadius: 3,
            background: theme.palette.mode === 'dark'
              ? `linear-gradient(135deg, ${alpha('#1e293b', 0.92)}, ${alpha('#0f172a', 0.9)})`
              : 'linear-gradient(135deg, #f8fafc 0%, #eef2ff 100%)',
            border: '1px solid',
            borderColor: theme.palette.mode === 'dark' ? 'rgba(148,163,184,0.15)' : '#e5e7eb',
            boxShadow: theme.palette.mode === 'dark'
              ? '0 16px 48px -28px rgba(0,0,0,0.6)'
              : '0 18px 48px -32px rgba(15,23,42,0.18)',
          })}
        >
          <Stack spacing={2} direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }} justifyContent="space-between">
            {!isMobile && (
              <Box>
                <Typography variant="h5" fontWeight={800} sx={{ letterSpacing: 0.2, color: 'text.primary' }}>
                  Compliance Progress
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Track overall compliance for the selected business
                </Typography>
              </Box>
            )}
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                width: '100%',
                maxWidth: 320,
                alignItems: 'center',
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Compliance Progress
                </Typography>
                <Box
                  sx={{
                    mt: 0.5,
                    height: 10,
                    borderRadius: 999,
                    backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    sx={{
                      width: `${summaryTotals.progress}%`,
                      height: '100%',
                      borderRadius: 999,
                      background: 'linear-gradient(90deg, #22c55e 0%, #16a34a 50%, #0ea5e9 100%)',
                      transition: 'width 200ms ease',
                    }}
                  />
                </Box>
              </Box>
              <Box sx={{ minWidth: 64, textAlign: 'right' }}>
                <Typography variant="h5" fontWeight={800}>
                  {complianceScore}%
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  MFS Compliance Score
                </Typography>
              </Box>
            </Box>
          </Stack>

          <Box sx={{ mt: 3 }}>
            <FilterRow
              showBusinessSelect={showBusinessSelect}
              businesses={businesses}
              selectedBusiness={selectedBusinessId}
              onBusinessChange={handleBusinessChange}
              onClearBusiness={handleClearBusiness}
              businessLoading={loading.businesses}
              branchOptions={filteredBranches}
              selectedBranch={branchFilter}
              onBranchChange={handleBranchChange}
              onClearBranch={handleClearBranchFilter}
              branchLoading={loading.branches}
              showBranchSelect={showBranchSelect}
            />
          </Box>

          <Box
            sx={{
              mt: 3,
              display: 'grid',
              gap: 2,
              gridTemplateColumns: {
                xs: 'repeat(1, minmax(0, 1fr))',
                sm: 'repeat(3, minmax(0, 1fr))',
                md: 'repeat(3, minmax(0, 1fr))',
                lg: 'repeat(5, minmax(0, 1fr))',
              },
            }}
          >
            {summarySections.map((section) => {
              const colors = CARD_PALETTE[section.index % CARD_PALETTE.length];
              const progress = section.eligibleCount
                ? Math.min(100, Math.round((section.uploadedCount / section.eligibleCount) * 100))
                : 0;
              return (
                <Box
                  key={section.key}
                  sx={(theme) => ({
                    flex: 1,
                    borderRadius: 4,
                    p: { xs: 1.5, sm: 2.5 },
                    border: '1px solid',
                    borderColor: theme.palette.mode === 'dark' ? 'divider' : '#e5e7eb',
                    backgroundColor: theme.palette.mode === 'dark' ? 'background.paper' : '#f8f9fb',
                    boxShadow: theme.palette.mode === 'dark'
                      ? '0 10px 20px -12px rgba(0,0,0,0.55)'
                      : '0 12px 28px -16px rgba(15,23,42,0.12)',
                    display: 'flex',
                    flexDirection: isMobile ? 'row' : 'column',
                    alignItems: 'center',
                    gap: isMobile ? 1.25 : 1,
                  })}
                >
                  <Box
                    sx={{
                      width: isMobile ? 52 : 64,
                      height: isMobile ? 52 : 64,
                      borderRadius: '50%',
                      backgroundColor: colors.from,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 0.75,
                      boxShadow: theme => theme.palette.mode === 'dark'
                        ? '0 8px 16px -10px rgba(0,0,0,0.65)'
                        : '0 10px 18px -12px rgba(0,0,0,0.18)',
                    }}
                    >
                      {colors.icon}
                    </Box>
                  <Stack spacing={0.5} sx={{ flex: 1, width: '100%', alignItems: isMobile ? 'flex-start' : 'center' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, letterSpacing: 0.2, textAlign: isMobile ? 'left' : 'center' }}>
                      {section.label}
                    </Typography>
                    <Box
                      sx={(theme) => ({
                        width: '100%',
                        mt: 0.25,
                        height: 8,
                        borderRadius: 999,
                        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
                        overflow: 'hidden',
                      })}
                    >
                      <Box
                        sx={{
                          width: `${progress}%`,
                          height: '100%',
                          borderRadius: 999,
                          background: 'linear-gradient(90deg, #4f46e5 0%, #6366f1 50%, #0ea5e9 100%)',
                          transition: 'width 200ms ease',
                        }}
                      />
                    </Box>
                    <Stack
                      direction="row"
                      spacing={0.75}
                      alignItems="center"
                      justifyContent={isMobile ? 'flex-start' : 'center'}
                    >
                      <Typography variant="h6" sx={{ fontWeight: 800 }}>
                        {section.uploadedCount}/{section.eligibleCount}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        ({progress}%)
                      </Typography>
                    </Stack>
                  </Stack>
                </Box>
              );
            })}
          </Box>
        </Paper>

        <StatsTiles
          statusCounts={statusCounts}
          totalBranches={totalBranchesCount}
          loading={loading.documents && !aggregatedDocuments.length}
          showTotalBranches={showTotalBranchesTile}
          onTileClick={handleStatsTileClick}
        />

        <ChartsRow
          statusCounts={statusCounts}
          branchComplianceData={branchComplianceData}
          loading={loading.documents}
          showBranchCompliance={showBranchComplianceChart}
        />

        <RecentExpiringTable
          rows={recentExpiringDocuments}
          branchMap={branchMap}
          loading={loading.documents}
          onRowClick={handleRecentDocumentClick}
        />
      </Stack>
    </Container>
  );
};

const DashboardLoadingHero = () => (
  <Box
    sx={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #0f172a 0%, #181357 40%, #312e81 100%)',
      color: '#fff',
      position: 'relative',
      overflow: 'hidden',
      px: 2,
    }}
  >
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        '&::before, &::after': {
          content: '""',
          position: 'absolute',
          width: 420,
          height: 420,
          borderRadius: '50%',
          filter: 'blur(80px)',
          opacity: 0.35,
        },
        '&::before': {
          top: -120,
          left: -80,
          background: 'radial-gradient(circle, rgba(79,70,229,0.7) 0%, transparent 70%)',
        },
        '&::after': {
          bottom: -160,
          right: -120,
          background: 'radial-gradient(circle, rgba(236,72,153,0.6) 0%, transparent 70%)',
        },
      }}
    />
    <Paper
      elevation={0}
      sx={{
        position: 'relative',
        px: { xs: 5, sm: 6 },
        py: { xs: 6, sm: 7 },
        borderRadius: '28px',
        background: 'rgba(15, 23, 42, 0.72)',
        backdropFilter: 'blur(18px)',
        border: '1px solid rgba(148, 163, 184, 0.2)',
        boxShadow: '0 35px 85px rgba(15, 23, 42, 0.45)',
        maxWidth: 520,
        width: '100%',
      }}
    >
      <Stack spacing={4} alignItems="center" textAlign="center">
        <Box sx={{ position: 'relative', width: 132, height: 132 }}>
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(129,140,248,0.55), rgba(236,72,153,0.45))',
              filter: 'blur(24px)',
              opacity: 0.8,
              animation: 'pulseGlow 3s ease-in-out infinite',
              '@keyframes pulseGlow': {
                '0%': { transform: 'scale(0.92)', opacity: 0.7 },
                '50%': { transform: 'scale(1.05)', opacity: 1 },
                '100%': { transform: 'scale(0.92)', opacity: 0.7 },
              },
            }}
          />
          <CircularProgress
            variant="determinate"
            value={100}
            size={120}
            thickness={3.2}
            sx={{ color: 'rgba(255,255,255,0.12)' }}
          />
          <CircularProgress
            variant="indeterminate"
            disableShrink
            size={120}
            thickness={3.2}
            sx={{
              color: '#818cf8',
              position: 'absolute',
              inset: 0,
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <RefreshIcon sx={{ fontSize: 38, color: '#e0e7ff' }} />
          </Box>
        </Box>
        <Stack spacing={1}>
          <Typography variant="h5" fontWeight={600} sx={{ color: '#f8fafc' }}>
            Preparing your compliance dashboard
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(226,232,240,0.72)' }}>
            We&rsquo;re syncing businesses, branches, and documentation so everything&rsquo;s ready for you.
          </Typography>
        </Stack>
        <Box sx={{ width: '100%' }}>
          <LinearProgress
            sx={{
              height: 6,
              borderRadius: 999,
              backgroundColor: 'rgba(148,163,184,0.18)',
              '& .MuiLinearProgress-bar': {
                borderRadius: 999,
                background: 'linear-gradient(90deg, #22d3ee 0%, #6366f1 50%, #ec4899 100%)',
              },
            }}
          />
        </Box>
        <Stack direction="row" spacing={1.5} sx={{ color: 'rgba(226,232,240,0.75)' }}>
          <LoadingPill>Businesses</LoadingPill>
          <LoadingPill>Branches</LoadingPill>
          <LoadingPill>Documents</LoadingPill>
        </Stack>
      </Stack>
    </Paper>
  </Box>
);

const LoadingPill = ({ children }) => (
  <Box
    sx={{
      px: 1.8,
      py: 0.6,
      fontSize: 12,
      letterSpacing: 0.4,
      borderRadius: 999,
      backgroundColor: 'rgba(148,163,184,0.16)',
      backdropFilter: 'blur(6px)',
      textTransform: 'uppercase',
    }}
  >
    {children}
  </Box>
);

const FilterRow = ({
  showBusinessSelect,
  businesses,
  selectedBusiness,
  onBusinessChange,
  onClearBusiness,
  businessLoading,
  branchOptions,
  selectedBranch,
  onBranchChange,
  onClearBranch,
  branchLoading,
  showBranchSelect,
}) => (
  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
    {showBusinessSelect && (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: { xs: '100%', md: 'auto' } }}>
        <FormControl sx={{ minWidth: { xs: '100%', md: 260 } }} disabled={businessLoading}>
          <InputLabel id="dashboard-business-select">Select Business</InputLabel>
          <Select
            labelId="dashboard-business-select"
            label="Select Business"
            value={selectedBusiness}
            onChange={(e) => onBusinessChange(e.target.value)}
          >
            {businesses.map(business => (
              <MenuItem key={business.businessId} value={business.businessId}>
                {deriveBusinessDisplayName(business)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Tooltip title="Clear business filter">
          <span>
            <IconButton
              size="small"
              onClick={onClearBusiness}
              disabled={!selectedBusiness || businessLoading}
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    )}
    {showBranchSelect && (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: { xs: '100%', md: 'auto' } }}>
        <FormControl sx={{ minWidth: { xs: '100%', md: 220 } }} disabled={branchLoading || !branchOptions.length}>
          <InputLabel id="dashboard-branch-select">Select Branch</InputLabel>
          <Select
            labelId="dashboard-branch-select"
            label="Select Branch"
            value={selectedBranch}
            onChange={(e) => onBranchChange(e.target.value)}
          >
            <MenuItem value="ALL">All Branches</MenuItem>
            {branchOptions.map(branch => (
              <MenuItem key={branch.branchId} value={branch.branchId}>
                {branch.nickname || branch.address}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Tooltip title="Clear branch filter">
          <span>
            <IconButton
              size="small"
              onClick={onClearBranch}
              disabled={selectedBranch === 'ALL'}
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    )}
  </Stack>
);

const StatsTiles = ({ statusCounts, totalBranches, loading, showTotalBranches, onTileClick }) => {
  const tiles = [
    {
      label: 'Compliant Documents',
      value: statusCounts.compliant,
      icon: <Avatar sx={{ bgcolor: 'success.light', color: 'success.dark' }}><InsertDriveFileIcon /></Avatar>,
      statusFilter: 'compliant',
    },
    {
      label: 'Expiring Soon',
      value: statusCounts.expiring,
      icon: <Avatar sx={{ bgcolor: 'warning.light', color: 'warning.dark' }}><AccessTimeIcon /></Avatar>,
      statusFilter: 'expiring',
    },
    {
      label: 'Expired Documents',
      value: statusCounts.expired,
      icon: <Avatar sx={{ bgcolor: 'error.light', color: 'error.dark' }}><DangerousIcon /></Avatar>,
      statusFilter: 'expired',
    },
  ];

  if (showTotalBranches) {
    tiles.push({
      label: 'Total Branches',
      value: totalBranches,
      icon: <Avatar sx={{ bgcolor: 'info.light', color: 'info.dark' }}><StorefrontIcon /></Avatar>,
      statusFilter: null,
    });
  }

  const tileCount = Math.max(tiles.length, 1);
  const isCompactTriple = !showTotalBranches && tileCount === 3;
  const columnsXs = isCompactTriple
    ? 'repeat(3, minmax(0, 1fr))'
    : `repeat(${Math.min(tileCount, 2)}, minmax(0, 1fr))`;
  const columnsSm = isCompactTriple
    ? 'repeat(3, minmax(0, 1fr))'
    : `repeat(${Math.min(tileCount, 3)}, minmax(0, 1fr))`;
  const columnsMd = `repeat(${Math.min(tileCount, 4)}, minmax(0, 1fr))`;
  const gapValue = isCompactTriple ? { xs: 1.25, sm: 1.5, md: 1.75, lg: 3 } : { xs: 1.5, sm: 1.5, md: 1.5, lg: 3 };
  const paddingValue = isCompactTriple
    ? { xs: 1.6, sm: 2.2, md: 3 }
    : { xs: 2, sm: 2.5, md: 3.5 };
  const iconSize = isCompactTriple
    ? { xs: 32, sm: 36, md: 44 }
    : { xs: 36, sm: 40, md: 48 };
  const valueFontSize = isCompactTriple
    ? { xs: '1.35rem', sm: '1.55rem', md: '1.85rem' }
    : { xs: '1.6rem', md: '1.9rem' };

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: columnsXs,
          sm: columnsSm,
          md: columnsMd,
          lg: columnsMd,
        },
        gap: gapValue,
      }}
    >
      {tiles.map(tile => (
        <Paper
          key={tile.label}
          elevation={0}
          sx={{
            borderRadius: 4,
            p: paddingValue,
            height: '100%',
            textAlign: 'center',
            backgroundColor: 'transparent',
            border: theme => theme.palette.mode === 'dark'
              ? '1px solid rgba(255, 255, 255, 1)'
              : '1px solid rgba(15, 23, 42, 1)',
            cursor: onTileClick ? 'pointer' : 'default',
            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            '&:hover': onTileClick ? {
              transform: 'translateY(-4px)',
              boxShadow: theme => theme.shadows[4],
            } : undefined,
          }}
          onClick={onTileClick ? () => onTileClick(tile) : undefined}
        >
          {loading ? (
            <Skeleton
              variant="rectangular"
              height={88}
              sx={{ borderRadius: 3 }}
            />
          ) : (
            <Stack spacing={{ xs: 1, md: 1.5 }} alignItems="center">
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                {React.cloneElement(tile.icon, {
                  sx: {
                    ...tile.icon.props.sx,
                    width: iconSize,
                    height: iconSize,
                  },
                })}
              </Box>
              <Typography
                variant="h5"
                fontWeight={700}
                sx={{ lineHeight: 1, fontSize: valueFontSize }}
              >
                {tile.value}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: { xs: '0.85rem', md: '0.95rem' }, maxWidth: { xs: 200, md: 'none' } }}
              >
                {tile.label}
              </Typography>
            </Stack>
          )}
        </Paper>
      ))}
    </Box>
  );
};

const ChartsRow = ({ statusCounts, branchComplianceData, loading, showBranchCompliance }) => {
  const statusData = [
    { name: 'Compliant', value: statusCounts.compliant, color: STATUS_COLORS.compliant },
    { name: 'Expiring Soon', value: statusCounts.expiring, color: STATUS_COLORS.expiring },
    { name: 'Expired', value: statusCounts.expired, color: STATUS_COLORS.expired },
  ].filter(item => item.value > 0);

  const complianceData = [
    { name: '100% Compliant', value: branchComplianceData.compliant, color: STATUS_COLORS.compliant },
    { name: 'Non-Compliant', value: branchComplianceData.nonCompliant, color: STATUS_COLORS.expired },
  ].filter(item => item.value > 0);

  const showComplianceCard = showBranchCompliance;
  const gridTemplateColumns = showComplianceCard
    ? {
        xs: '1fr',
        sm: 'repeat(2, minmax(0, 1fr))',
        md: 'repeat(2, minmax(0, 1fr))',
        lg: 'repeat(2, minmax(0, 1fr))',
      }
    : {
        xs: '1fr',
        sm: '1fr',
        md: '1fr',
        lg: '1fr',
      };

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns,
        gap: 2,
      }}
    >
      <ChartCard title="Document Status Overview" loading={loading}>
          {statusData.length === 0 ? (
            <EmptyChartState />
          ) : (
            <SimplePie data={statusData} />
          )}
      </ChartCard>
      {showComplianceCard && (
        <ChartCard title="Branch Compliance Status" loading={loading}>
            {complianceData.length === 0 ? (
              <EmptyChartState />
            ) : (
              <SimplePie data={complianceData} />
            )}
        </ChartCard>
      )}
    </Box>
  );
};

const ChartCard = ({ title, children, loading }) => (
  <Paper
    elevation={0}
    sx={{
      borderRadius: 4,
      p: 3,
      minHeight: 320,
      border: theme => theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(15,23,42,0.08)',
      display: 'flex',
      flexDirection: 'column',
      flexGrow: 1,
      width: '100%',
    }}
  >
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
      <Typography variant="h6">{title}</Typography>
      {loading && <RefreshIcon fontSize="small" color="action" />}
    </Stack>
    <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'stretch', justifyContent: 'stretch' }}>
      {loading ? (
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Skeleton variant="circular" width={180} height={180} />
        </Box>
      ) : (
        <Box sx={{ width: '100%', display: 'flex' }}>
          {children}
        </Box>
      )}
    </Box>
  </Paper>
);

const EmptyChartState = () => (
  <Stack spacing={1} alignItems="center" justifyContent="center" sx={{ textAlign: 'center' }}>
    <Typography variant="body2" color="text.secondary">No data available</Typography>
  </Stack>
);

const SimplePie = ({ data }) => {
  const total = data.reduce((sum, item) => sum + item.value, 0);
  if (total === 0) {
    return <EmptyChartState />;
  }

  let currentAngle = 0;
  const segments = data.map(item => {
    const startAngle = currentAngle;
    const sweep = (item.value / total) * 360;
    currentAngle += sweep;
    return {
      ...item,
      start: startAngle,
      end: currentAngle,
    };
  });

  const gradient = segments
    .map(segment => `${segment.color} ${segment.start}deg ${segment.end}deg`)
    .join(', ');

  return (
    <Stack
      spacing={2}
      alignItems="center"
      justifyContent="center"
      sx={{ width: '100%', height: '100%', flexGrow: 1 }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: { xs: 320, md: 420, lg: 480 },
          aspectRatio: '1 / 1',
          borderRadius: '50%',
          background: `conic-gradient(${gradient})`,
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            width: '55%',
            height: '55%',
            borderRadius: '50%',
            backgroundColor: 'background.default',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography variant="subtitle2">{total} total</Typography>
        </Box>
      </Box>
      <Stack spacing={1}>
        {segments.map(segment => (
          <Stack key={segment.name} direction="row" spacing={1} alignItems="center">
            <Box sx={{ width: 12, height: 12, borderRadius: 0.5, bgcolor: segment.color }} />
            <Typography variant="body2">{segment.name} &middot; {segment.value}</Typography>
          </Stack>
        ))}
      </Stack>
    </Stack>
  );
};

const RecentExpiringTable = ({ rows, branchMap, loading, onRowClick }) => (
  <Paper
    elevation={0}
    sx={{
      borderRadius: 4,
      border: theme => theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(15,23,42,0.08)',
    }}
  >
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>Recent Expiring Documents</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Documents expiring in the next 30 days
      </Typography>
      {loading ? (
        <Skeleton variant="rectangular" height={180} />
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Branch</TableCell>
                <TableCell>Document</TableCell>
                <TableCell>Expiration Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Days Left</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">No documents expiring soon.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row, index) => {
                  const branch = branchMap.get(row.branchId);
                  return (
                    <TableRow
                      key={`${row.documentTitle}-${index}`}
                      hover={Boolean(onRowClick)}
                      sx={{ cursor: onRowClick ? 'pointer' : 'default' }}
                      onClick={() => onRowClick?.(row)}
                    >
                      <TableCell>{branch?.nickname || branch?.address || 'Unknown Branch'}</TableCell>
                      <TableCell>{row.documentTitle}</TableCell>
                      <TableCell>{formatDate(row.expirationDate)}</TableCell>
                      <TableCell>{statusEmoji(resolveDocumentStatus(row.expirationDate))}</TableCell>
                      <TableCell>{`${row.diffDays} day${row.diffDays === 1 ? '' : 's'}`}</TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  </Paper>
);

const resolveDocumentStatus = (dateString) => {
  if (!dateString) return 'unknown';
  const expire = new Date(dateString);
  if (Number.isNaN(expire.getTime())) return 'unknown';
  const today = new Date();
  const diffDays = Math.floor((expire - today) / (1000 * 60 * 60 * 24));
  if (diffDays < 0) return 'expired';
  if (diffDays <= 30) return 'expiring';
  return 'compliant';
};

const statusEmoji = (status) => {
  switch (status) {
    case 'expired':
      return '⛔️';
    case 'expiring':
      return '⚠️';
    case 'compliant':
      return '✅';
    default:
      return '—';
  }
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return 'Invalid Date';
  return date.toLocaleDateString();
};

// Report types employees can create (excludes Food Handler's Personal Hygiene which is Supervisor-only)
const EMPLOYEE_REPORT_TYPES = [REPORT_TYPES.RAW_MATERIAL_INSPECTION, REPORT_TYPES.SUPPLIER_RECORD];

const EmployeeDashboard = () => {
  const { userBusinessId, userBranchIds, userContactId } = useAuth();
  const navigate = useNavigate();
  const [branches, setBranches] = useState([]);
  const [todayReports, setTodayReports] = useState({});
  const [loading, setLoading] = useState(true);
  const [showDone, setShowDone] = useState(false);

  const today = useMemo(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() + 1, day: now.getDate() };
  }, []);

  const todayLabel = useMemo(() => {
    return new Date().toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  }, []);

  useEffect(() => {
    if (!userBusinessId) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const branchResponse = await getBranches(userBusinessId);
        if (cancelled) return;
        const allBranches = (branchResponse.data || []).filter(b => (b.status || 'ACTIVE') !== 'INACTIVE');
        const employeeBranches = userBranchIds.length > 0
          ? allBranches.filter(b => userBranchIds.includes(b.branchId))
          : allBranches.filter(b => userContactId && (b.employeeIds || []).includes(userContactId));
        setBranches(employeeBranches);

        const reportMap = {};
        await Promise.all(
          employeeBranches.map(async branch => {
            try {
              const res = await listReports(branch.branchId, userBusinessId);
              const items = res.data || [];
              const todayItems = items.filter(
                r => r.year === today.year && r.month === today.month && r.day === today.day
              );
              EMPLOYEE_REPORT_TYPES.forEach(type => {
                const key = `${branch.branchId}:${type}`;
                const submitted = todayItems.filter(r => r.reportType === type);
                reportMap[key] = submitted;
              });
            } catch (_) {
              EMPLOYEE_REPORT_TYPES.forEach(type => {
                reportMap[`${branch.branchId}:${type}`] = [];
              });
            }
          })
        );
        if (cancelled) return;
        setTodayReports(reportMap);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [userBusinessId, userBranchIds, userContactId, today]);

  const handleReportClick = (branchId, reportType) => {
    navigate('/reports', { state: { branchId, businessId: userBusinessId, reportType, today } });
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 3 }} />
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: { xs: 3, md: 5 } }}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h5" fontWeight={800}>Today&rsquo;s Reports</Typography>
          <Typography variant="body2" color="text.secondary">{todayLabel}</Typography>
        </Box>

        <FormControlLabel
          control={<Checkbox checked={showDone} onChange={e => setShowDone(e.target.checked)} />}
          label="Show completed reports"
        />

        {branches.length === 0 && (
          <Alert severity="info">You are not assigned to any branch yet.</Alert>
        )}

        {branches.map(branch => {
          const items = EMPLOYEE_REPORT_TYPES.map(type => {
            const key = `${branch.branchId}:${type}`;
            const submitted = todayReports[key] || [];
            return { type, submitted, isDone: submitted.length > 0 };
          });

          const visibleItems = showDone ? items : items.filter(i => !i.isDone);
          if (visibleItems.length === 0) return null;

          return (
            <Paper key={branch.branchId} elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
              <Box sx={{ px: 2.5, py: 1.5, bgcolor: 'action.hover', borderBottom: '1px solid', borderColor: 'divider' }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <StorefrontIcon fontSize="small" color="action" />
                  <Typography variant="subtitle1" fontWeight={700}>{branch.nickname || branch.address}</Typography>
                </Stack>
              </Box>
              <Stack>
                {visibleItems.map(({ type, submitted, isDone }) => (
                  <Box
                    key={type}
                    onClick={() => handleReportClick(branch.branchId, type)}
                    sx={{
                      px: 2.5,
                      py: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      cursor: 'pointer',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      '&:last-child': { borderBottom: 'none' },
                      '&:hover': { bgcolor: 'action.hover' },
                      transition: 'background-color 0.15s ease',
                    }}
                  >
                    {isDone
                      ? <CheckCircleIcon color="success" />
                      : <RadioButtonUncheckedIcon color="action" />
                    }
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body1" fontWeight={500} color={isDone ? 'text.secondary' : 'text.primary'}>
                        {REPORT_TYPE_LABELS[type]}
                      </Typography>
                      {isDone && (
                        <Typography variant="caption" color="text.secondary">
                          {submitted.length} {submitted.length === 1 ? 'entry' : 'entries'} submitted today
                        </Typography>
                      )}
                    </Box>
                    {isDone && (
                      <Chip label="Done" size="small" color="success" variant="outlined" />
                    )}
                  </Box>
                ))}
              </Stack>
            </Paper>
          );
        })}

        {!loading && branches.length > 0 && !showDone && Object.keys(todayReports).length > 0 && (
          EMPLOYEE_REPORT_TYPES.every(type =>
            branches.every(b => (todayReports[`${b.branchId}:${type}`] || []).length > 0)
          ) && (
            <Alert severity="success" icon={<CheckCircleIcon />}>
              All reports for today are complete!
            </Alert>
          )
        )}
      </Stack>
    </Container>
  );
};

export default DashboardPage;
